import React, { forwardRef, HTMLProps } from 'react';
import { Button } from '@saucelabs/ds-button';
import { Typography } from '@saucelabs/ds-typography';
import { IconCheckmarkCircleFill } from '@saucelabs/ds-icons';
import { Badge } from '@saucelabs/ds-badge/lib';
import { Link } from '@saucelabs/ds-link';
import { Card, CardProps } from '@saucelabs/ds-card';

import { FeatureList } from './Plans/FeatureList';
import { amountSeparator } from '../utils/numbers';

interface PlanPurchaseCardRateProps extends HTMLProps<HTMLDivElement> {
  rate: number,
  billingPeriod?: PlanInterval,
  forceMonthlyPricing?: boolean;
}

export const PlanPurchaseCardRate = forwardRef<HTMLDivElement, PlanPurchaseCardRateProps>(({
  rate,
  billingPeriod,
  forceMonthlyPricing,
  ...props
}, ref) => (
  <div
    ref={ref}
    className="text-left text-high-emphasis mt-3"
    {...props}
  >
    <Typography
      className="font-semibold mt-0 mb-0"
      disableSpacing
      variant="heading-2"
    >
      {amountSeparator(rate)}
      {rate > 0 && (
        <>
          <Typography
            className="text-high-emphasis text-left font-normal"
            component="span"
            variant="body-large"
          >
            {forceMonthlyPricing ?
              '/month, billed annually' :
              `/${billingPeriod as string}`}
          </Typography>
          {' '}
          <Typography
            className="text-medium-emphasis text-left font-normal"
            component="span"
            variant="body"
          >
            + taxes and fees
          </Typography>
        </>
      )}
    </Typography>
  </div>
));

export interface PlanPurchaseCardProps {
  heading: string;
  handleContinue?: () => void;
  dropdown?: JSX.Element;
  rate?: number;
  subheading?: string;
  taglines?: React.ReactNode[];
  includes?: React.ReactNode[];
  savings?: string;
  isCurrentPlan?: boolean;
  billingPeriod?: PlanInterval;
  continueText?: string;
  forceMonthlyPricing?: boolean;
  seeAllFeaturesLink?: string;
  variant?: CardProps['variant']
  icon?: React.ElementType<React.SVGProps<SVGElement>>;
}

export const PlanPurchaseCard: React.FC<PlanPurchaseCardProps> = ({
  handleContinue,
  dropdown,
  heading,
  subheading,
  rate,
  taglines,
  includes,
  savings,
  isCurrentPlan,
  billingPeriod,
  continueText = 'Subscribe to this Plan',
  forceMonthlyPricing = false,
  seeAllFeaturesLink,
  variant = 'bordered',
  icon: Icon,
}) => (
  <Card
    className={`
      flex flex-col p-3 h-full w-full
      ${savings ? 'border-green-400 border-solid border-2' : 'border-transparent'}`}
    data-qa="plan-purchase-card"
    variant={variant}
  >
    {savings && (
      <Badge
        className="-mt-4.5 mb-2 px-2 mx-auto"
        color="green"
        emphasis="high"
      >
        {savings}
      </Badge>
    )}
    <Typography
      className="text-high-emphasis mt-0 mb-1 flex flex-row items-center"
      disableSpacing
      variant="heading-3"
    >
      {Icon && (
        <Icon className="mr-1 fill-current text-gray-500" />
      )}
      {heading}
    </Typography>
    {subheading && (
      <Typography
        className="text-medium-emphasis mt-0"
        disableSpacing
        variant="body-large"
      >
        {subheading}
      </Typography>
    )}
    {Number.isFinite(rate) && (
      <PlanPurchaseCardRate
        billingPeriod={billingPeriod}
        data-qa={isCurrentPlan ? 'plan-monthly-rate-current-plan' : 'plan-monthly-rate'}
        forceMonthlyPricing={forceMonthlyPricing}
        rate={rate as number}
      />
    )}
    {dropdown}
    <div>
      {taglines?.map((tagline, i) => (
        <Typography
          key={tagline}
          className={(!dropdown && i === 0) ? 'mt-2 mb-5' : 'mt-3'}
          variant="body-large"
        >
          {tagline}
        </Typography>
      ))}
    </div>
    {isCurrentPlan ? (
      <Button
        block
        className="text-center mt-3 mb-4"
        color="secondary"
        disabled
        elementType="button"
        isDisabled
        variant="outline"
      >
        Your Current Plan
      </Button>
    ) : (
      <Button
        block
        className="text-center mt-3 mb-4"
        color="primary"
        data-qa="plan-purchase-continue"
        onPress={handleContinue}
        variant="contained"
      >
        {continueText}
      </Button>
    )}
    {includes && (
      <>
        <Typography
          className="mb-1"
          variant="overline"
        >
          Includes
        </Typography>
        <FeatureList
          isColumn
          marginless
        >
          {includes?.map((included, i) => (
            <li
              key={`${typeof included === 'string' ? included : i}`}
              className={`flex items-start ${i === 0 ? 'mb-1' : 'my-1'} pt-1 border-0 border-t border-solid border-gray-200`}
            >
              <IconCheckmarkCircleFill className="mt-0.25 flex-shrink-0 fill-current text-green-600 text-base" />
              <Typography
                className="text-high-emphasis"
                component="span"
                variant="list"
              >
                {included}
              </Typography>
            </li>
          ))}
        </FeatureList>
        {seeAllFeaturesLink && (
          <div className="mt-3">
            <Link
              isExternal
              target="_blank"
              to={seeAllFeaturesLink}
            >
              See all features
            </Link>
          </div>
        )}
      </>
    )}
  </Card>
);
